import { ChangeDesc, ChangeSet, Text } from "@codemirror/state";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { action, computed, reaction, runInAction, set } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { logEvent } from "../Amplitude";
import { atom, deepAtom } from "../atom";
import {
  SERVER_SOCKET_HOST,
  SocketCloseCodes,
  SOCKET_PING_MESSAGE,
  SOCKET_PROTOCOL_VERSION,
  UNKNOWN_CLIENT_ID,
} from "../Constants";
import { canvasSelectionAtom } from "../SharedIframeState";
import {
  Action,
  ActionMeta,
  ActionType,
  Canvas,
  CanvasData,
  ClientMessage,
  ClientMessageType,
  ImportModule,
  Pane,
  PaneType,
  PatchOp,
  PatchPath,
  ServerMessage,
  ServerMessageType,
} from "../Types";
import { applyTextChangeSet, areArraysEqual, assert, debounce } from "../Utils";
import { AboutButton } from "./AboutButton";
import { canvasDispatch } from "./CanvasDispatch";
import {
  canvasAtom,
  focusedPaneIdAtom,
  initialSearchParamEntriesBox,
  isSafeModeAtom,
  shouldClonePaneInputsBox,
  urlLayoutIdAtom,
  usedDefaultLayoutAtom,
} from "./CanvasState";
import { setupCanvasStateEffects } from "./CanvasStateEffects";
import { ClipboardPane } from "./ClipboardPane";
import { getTypeModuleNameForImport } from "./CodeAnalysis";
import { CommandBar } from "./CommandBar";
import {
  codeWorkerCall,
  PaneEditorRemoteUpdateEmitter,
} from "./ExpressionEditor";
import { initializeSandboxFrame, ParentComlinkAPI } from "./FrameSandboxAPI";
import { keyboardCommandsMobx, setupKeyboardListeners } from "./Keyboard";
import { setupKeyboardEffects } from "./KeyboardEffects";
import { PaneCanvas } from "./PaneCanvas";
import { OpenAISetupDialogController } from "./PaneLLM";
import { presenceState, updateWithPresenceUpdate } from "./Presence";
import { applicationState, socketStateAtom } from "./Sync";
import { TemplateBrowserPane } from "./TemplateBrowserPane";
import { ViewportMarksPanel } from "./ViewportMarksPanel";

setupCanvasStateEffects();
const unsubscribes = [setupKeyboardListeners(), setupKeyboardEffects()];
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  });
}

window.React = React;
// @ts-ignore
window.__NATTO__ = {
  showNavigationDialog: () => {
    ParentComlinkAPI.showCanvasSelector();
  },
  showGalleryDialog: () => {
    ParentComlinkAPI.showGalleryDialog();
  },
  navigateToTutorial: () => {
    ParentComlinkAPI.navigateCanvas(["tutorial", "tip-calculator"]);
  },
  onVideoPlay: () => {
    logEvent("play intro video");
  },
  keyboardCommandsMobx: keyboardCommandsMobx,
  shouldClonePaneInputsBox,
  EXPERIMENTAL: {
    loadTypedef: (
      args: [
        paneModule: ImportModule,
        root: string,
        filePath: string,
        loadFilePath?: string | undefined
      ][]
    ) => {
      return Promise.all(
        args.map((arg) =>
          codeWorkerCall({
            type: "LoadTypedef",
            typeModuleName: getTypeModuleNameForImport(arg[0]),
            cdnTypeRoot: arg[1],
            filePath: arg[2],
            loadFilePath: arg[3],
          })
        )
      );
    },
  },
};

// @ts-ignore
window.$natto = {
  getURLSearchParams: () => initialSearchParamEntriesBox.get(),
  experimental: {
    openFileDirectory: ParentComlinkAPI.openFileDirectory,
    atom,
    deepAtom,
  },
};
// @ts-ignore
window.__NATTO_EXPERIMENTAL = window.$natto.experimental;

const NonSyncCanvasApp = observer(
  ({ initialCanvas }: { initialCanvas: Canvas }) => {
    const canvasSelection = canvasSelectionAtom.value;
    const canvasId = initialCanvas.id;

    return (
      <PaneCanvas
        canvasId={canvasId}
        tutorialId={
          canvasSelection !== undefined && canvasSelection[0] === "tutorial"
            ? canvasSelection[1]
            : undefined
        }
        isSynced={false}
      />
    );
  }
);

function applyPatch(
  op: PatchOp,
  document: any,
  path: PatchPath,
  value?: any
): any {
  if (op === PatchOp.CodemirrorUpdate) {
    const [_, paneIdElement, paneField] = path;
    assert(Array.isArray(paneIdElement) && typeof paneField === "string");
    const [paneId] = paneIdElement;
    const { c: changesJSON, cl: updateClientId } = value;
    const canvas = document;
    const panes = canvas.panes.map((pane: Pane) => {
      if (pane.id === paneId) {
        if (paneField === "expression") {
          assert(
            pane.type === PaneType.Evaluate ||
              pane.type === PaneType.EvaluateGlobal
          );
          // TODO: optimize this by caching Text
          const prevDoc = Text.of(pane.expression.split("\n"));
          const changes = ChangeSet.fromJSON(changesJSON);
          const expression = changes.apply(prevDoc).toString();
          return {
            ...pane,
            expression,
          };
        } else if (paneField === "text") {
          assert(pane.type === PaneType.InputText);
          // TODO: optimize this by caching Text
          const prevDoc = Text.of(pane.text.split("\n"));
          const changes = ChangeSet.fromJSON(changesJSON);
          const text = changes.apply(prevDoc).toString();
          return {
            ...pane,
            text,
          };
        }
      }
      return pane;
    });
    return {
      ...canvas,
      panes,
    };
  }

  const [head, ...tail] = path;
  if (path.length === 1) {
    switch (op) {
      case PatchOp.Add: {
        if (head === "-" && Array.isArray(document)) {
          return [...document, value];
        }
        assert(typeof head === "string");
        return {
          ...document,
          [head]: value,
        };
      }
      case PatchOp.Remove: {
        if (Array.isArray(document)) {
          if (Array.isArray(head)) {
            return document.filter((entry) => entry.id !== head[0]);
          }
          assert(typeof head === "number");
          const rv = [...document];
          rv.splice(head, 1);
          return rv;
        }
        assert(typeof head === "string");
        const rv = { ...document };
        delete rv[head];
        return rv;
      }
      case PatchOp.Replace: {
        if (Array.isArray(document)) {
          assert(typeof head === "number");
          return document.map((entry, i) => (i === head ? value : entry));
        }
        assert(typeof head === "string");
        return {
          ...document,
          [head]: value,
        };
      }
    }
  }
  if (Array.isArray(document)) {
    if (Array.isArray(head)) {
      return document.map((entry) =>
        entry.id === head[0] ? applyPatch(op, entry, tail, value) : entry
      );
    }
    assert(typeof head === "number");
    return document.map((entry, i) =>
      i === head ? applyPatch(op, entry, tail, value) : entry
    );
  }
  assert(typeof document === "object" && typeof head === "string");
  return {
    ...document,
    [head]: applyPatch(op, document[head] ?? {}, tail, value),
  };
}

// This is a backup method for computing a changeDesc if we don't have the
// changeDesc history available from the server.
function computeChangeDescFromText(
  prevText: string,
  newText: string
): ChangeDesc {
  let matchingPrefixLength = 0;
  const shorterLength = Math.min(prevText.length, newText.length);
  while (
    matchingPrefixLength < shorterLength &&
    prevText.charCodeAt(matchingPrefixLength) ===
      newText.charCodeAt(matchingPrefixLength)
  ) {
    matchingPrefixLength++;
  }
  if (
    matchingPrefixLength === shorterLength &&
    prevText.length === newText.length
  ) {
    return ChangeDesc.fromJSON([matchingPrefixLength, -1]);
  }
  let prevMatchingSuffixIndex = prevText.length;
  let newMatchingSuffixIndex = newText.length;
  while (
    prevMatchingSuffixIndex > 0 &&
    newMatchingSuffixIndex > 0 &&
    prevText.charCodeAt(prevMatchingSuffixIndex - 1) ===
      newText.charCodeAt(newMatchingSuffixIndex - 1)
  ) {
    prevMatchingSuffixIndex--;
    newMatchingSuffixIndex--;
  }
  if (
    prevMatchingSuffixIndex < matchingPrefixLength &&
    prevText.length < newText.length
  ) {
    newMatchingSuffixIndex =
      matchingPrefixLength + (newMatchingSuffixIndex - prevMatchingSuffixIndex);
    prevMatchingSuffixIndex = matchingPrefixLength;
  } else if (newMatchingSuffixIndex < matchingPrefixLength) {
    prevMatchingSuffixIndex =
      matchingPrefixLength + (prevMatchingSuffixIndex - newMatchingSuffixIndex);
    newMatchingSuffixIndex = matchingPrefixLength;
  }
  return ChangeDesc.fromJSON(
    [
      matchingPrefixLength > 0 ? [matchingPrefixLength, -1] : [],
      [
        prevMatchingSuffixIndex - matchingPrefixLength,
        newMatchingSuffixIndex - matchingPrefixLength,
      ],
      prevText.length > prevMatchingSuffixIndex
        ? [prevText.length - prevMatchingSuffixIndex, -1]
        : [],
    ].flat()
  );
}

function SocketAppImpl({}: {}) {
  const clientIdRef = useRef<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<
    string | true | [message: string] | undefined
  >(undefined);
  const numSocketErrorsRef = useRef(0);
  const socketRef = useRef<ReconnectingWebSocket | undefined>(undefined);

  useEffect(
    action(() => {
      const socket = new ReconnectingWebSocket(
        async () => {
          const localActions = applicationState.localActions;
          let updatePaneCodemirrorBaseVersion: number | undefined;
          for (const [action, _] of localActions) {
            if (action[0] === ActionType.UpdatePaneCodemirror) {
              const actionBaseVersion = action[1].v;
              if (updatePaneCodemirrorBaseVersion !== undefined) {
                if (actionBaseVersion !== updatePaneCodemirrorBaseVersion) {
                  throw new Error("multiple baseVersion");
                }
              } else {
                updatePaneCodemirrorBaseVersion = actionBaseVersion;
              }
            }
          }
          let socketToken = await ParentComlinkAPI.getSocketToken();
          if (socketToken === undefined) {
            // hardcode one retry here for when computer waking up from sleep
            await new Promise<void>((resolve) =>
              setTimeout(() => resolve(), 3000)
            );
            socketToken = await ParentComlinkAPI.getSocketToken();
          }
          if (socketToken === undefined) {
            console.error("could not get socketToken");
            setErrorMessage(["natto had trouble connecting to server."]);
            // unset _connectLock so ReconnectingWebSocket can connect again.
            // @ts-ignore
            socket._connectLock = false;
            throw new Error();
          }
          return `${SERVER_SOCKET_HOST}/?token=${socketToken}&v=${SOCKET_PROTOCOL_VERSION}${
            clientIdRef.current !== undefined
              ? `&clientId=${clientIdRef.current}`
              : ""
          }${
            updatePaneCodemirrorBaseVersion !== undefined
              ? `&changeDescFrom=${updatePaneCodemirrorBaseVersion}`
              : ""
          }`;
        },
        undefined,
        { maxRetries: 32, maxEnqueuedMessages: 0 }
      );
      socketRef.current = socket;
      socketStateAtom.value = "connecting";
      socket.addEventListener(
        "open",
        action(() => {
          socketStateAtom.value = "connected";
        })
      );
      socket.addEventListener(
        "close",
        action((e) => {
          logEvent("close socket", { code: e.code, reason: e.reason });
          if (e.code === SocketCloseCodes.InviteRevoked) {
            setErrorMessage("The invite was revoked.");
            socket.close();
          } else if (e.code >= 4000) {
            console.error(e);
            setErrorMessage(true);
            socket.close();
          }
          socketStateAtom.value = "disconnected";
        })
      );
      socket.addEventListener(
        "error",
        action(() => {
          socketStateAtom.value = "disconnected";
        })
      );

      function reconnectAfterSocketError() {
        numSocketErrorsRef.current = numSocketErrorsRef.current + 1;
        if (numSocketErrorsRef.current < 8) {
          socket.reconnect();
        } else {
          console.error("exhausted reconnecting after error");
          setErrorMessage(true);
          socket.close();
        }
      }

      socket.addEventListener(
        "message",
        action((event: MessageEvent) => {
          const message: ServerMessage = JSON.parse(event.data);
          switch (message[0]) {
            case ServerMessageType.Init: {
              const {
                c: serverCanvas,
                p: serverPresenceState,
                v: serverVersion,
                cl: clientId,
                la: lastActionId,
                rc: remoteChanges,
              } = message[1];
              clientIdRef.current = clientId;
              let { server: prevServer, localActions } = applicationState;
              const layoutId = urlLayoutIdAtom.value;
              if (
                prevServer === undefined &&
                layoutId === undefined &&
                serverCanvas.defaultLayoutId !== undefined &&
                serverCanvas.layouts.some(
                  (layout) => layout.id === serverCanvas.defaultLayoutId
                ) &&
                layoutId !== serverCanvas.defaultLayoutId
              ) {
                // setting sandbox layoutId state here to avoid flash
                urlLayoutIdAtom.value = serverCanvas.defaultLayoutId;
                usedDefaultLayoutAtom.value = true;
                ParentComlinkAPI.switchLayoutId(serverCanvas.defaultLayoutId);
              }
              let confirmedLocalActions: [Action, ActionMeta][] = [];
              if (lastActionId !== undefined) {
                confirmedLocalActions = localActions.filter(
                  ([, actionMeta]) => actionMeta[0] <= lastActionId
                );
                localActions = localActions.filter(
                  ([, actionMeta]) => actionMeta[0] > lastActionId
                );
              }
              const remoteChangesByPaneId: {
                [paneId: string]: { [paneField: string]: ChangeDesc | false };
              } = {};
              try {
                if (remoteChanges !== undefined) {
                  // rebase local UpdatePaneCodemirror actions
                  localActions = localActions.map((localAction) => {
                    const [action, actionMeta] = localAction;
                    if (action[0] === ActionType.UpdatePaneCodemirror) {
                      const { paneId, f: paneField, v } = action[1];
                      if (remoteChangesByPaneId[paneId] === undefined) {
                        remoteChangesByPaneId[paneId] = {};
                      }
                      if (
                        remoteChangesByPaneId[paneId][paneField] === undefined
                      ) {
                        if (remoteChanges[paneId]?.[paneField] !== undefined) {
                          remoteChangesByPaneId[paneId][paneField] =
                            ChangeDesc.fromJSON(
                              remoteChanges[paneId][paneField]
                            );
                        } else {
                          const serverPane = serverCanvas.panes.find(
                            (p) => p.id === paneId
                          );
                          if (serverPane !== undefined) {
                            const prevServerPane = prevServer![0].panes.find(
                              (p) => p.id === paneId
                            );
                            assert(
                              (paneField === "expression" &&
                                (serverPane.type === PaneType.Evaluate ||
                                  serverPane.type ===
                                    PaneType.EvaluateGlobal) &&
                                (prevServerPane?.type === PaneType.Evaluate ||
                                  prevServerPane?.type ===
                                    PaneType.EvaluateGlobal)) ||
                                (paneField === "text" &&
                                  serverPane.type === PaneType.InputText &&
                                  prevServerPane?.type === PaneType.InputText)
                            );
                            // @ts-ignore
                            let prevServerPaneValue = prevServerPane[paneField];
                            for (const [action] of confirmedLocalActions) {
                              if (
                                action[0] === ActionType.UpdatePaneCodemirror &&
                                action[1].paneId === paneId
                              ) {
                                prevServerPaneValue = applyTextChangeSet(
                                  prevServerPaneValue,
                                  action[1].changes
                                );
                              }
                            }
                            // @ts-ignore
                            const serverPaneValue = serverPane[paneField];
                            if (serverPaneValue !== prevServerPaneValue) {
                              remoteChangesByPaneId[paneId][paneField] =
                                computeChangeDescFromText(
                                  prevServerPaneValue,
                                  serverPaneValue
                                );
                            } else {
                              remoteChangesByPaneId[paneId][paneField] = false;
                            }
                          } else {
                            remoteChangesByPaneId[paneId][paneField] = false;
                          }
                        }
                      }
                      let newChangeSetJSON = action[1].changes;
                      const remoteChangeDesc =
                        remoteChangesByPaneId[paneId][paneField];
                      if (remoteChangeDesc !== false) {
                        const localChangeSet = ChangeSet.fromJSON(
                          action[1].changes
                        );
                        newChangeSetJSON = localChangeSet
                          .mapDesc(remoteChangeDesc)
                          .toJSON();
                        remoteChangesByPaneId[paneId][paneField] =
                          remoteChangeDesc.mapDesc(localChangeSet, true);
                      }
                      return [
                        [
                          action[0],
                          {
                            ...action[1],
                            v: serverVersion,
                            changes: newChangeSetJSON,
                          },
                        ],
                        actionMeta,
                      ];
                    }
                    return localAction;
                  });
                }
              } catch (e) {
                console.error(e);
                setErrorMessage(true);
                throw e;
              }
              const prevCanvas = applicationState.canvas;
              applicationState.server = [serverCanvas, serverVersion];
              applicationState.clientId = clientId;
              applicationState.localActions = localActions;
              {
                const newCanvas = applicationState.canvas;
                for (const prevPane of prevCanvas.panes) {
                  if (
                    prevPane.type === PaneType.Evaluate ||
                    prevPane.type === PaneType.EvaluateGlobal
                  ) {
                    const newPane = newCanvas.panes.find(
                      (p) => p.id === prevPane.id
                    );
                    if (
                      (newPane?.type === PaneType.Evaluate ||
                        newPane?.type === PaneType.EvaluateGlobal) &&
                      newPane.expression !== prevPane.expression
                    ) {
                      PaneEditorRemoteUpdateEmitter.emit(newPane.id, {
                        paneField: "expression",
                        changes: ["value", newPane.expression],
                      });
                    }
                  }
                  if (prevPane.type === PaneType.InputText) {
                    const newPane = newCanvas.panes.find(
                      (p) => p.id === prevPane.id
                    );
                    if (
                      newPane?.type === PaneType.InputText &&
                      newPane.text !== prevPane.text
                    ) {
                      PaneEditorRemoteUpdateEmitter.emit(newPane.id, {
                        paneField: "text",
                        changes: ["value", newPane.text],
                      });
                    }
                  }
                }
              }
              if (localActions.length > 0) {
                pushLocalActionsDebounced(localActions);
              }
              if (serverPresenceState !== undefined) {
                set(presenceState, serverPresenceState);
              }
              break;
            }
            case ServerMessageType.Identify: {
              const { cl: clientId, la: lastActionId } = message[1];
              clientIdRef.current = clientId;
              applicationState.clientId = clientId;
              applicationState.localActions =
                applicationState.localActions.filter(
                  ([, actionMeta]) => actionMeta[0] > lastActionId
                );
              break;
            }
            case ServerMessageType.Update: {
              const {
                cl: clientId,
                la: lastActionId,
                p: patches,
                pu: presenceUpdate,
                v: newServerVersion,
              } = message[1];
              if (applicationState.server === undefined) {
                throw new Error();
              }
              if (
                newServerVersion !== undefined &&
                applicationState.server[1] !== newServerVersion - 1
              ) {
                console.error(
                  "server version mismatch",
                  applicationState.server,
                  newServerVersion
                );
                logEvent("error", {
                  type: "server version mismatch",
                  metadata: JSON.stringify({
                    local: applicationState.server[1],
                    server: newServerVersion,
                  }),
                });
                reconnectAfterSocketError();
                return;
              }
              let newServerValue = applicationState.server[0];
              if (patches !== undefined) {
                for (const patch of patches) {
                  try {
                    newServerValue = applyPatch(
                      patch[0],
                      newServerValue,
                      patch[1],
                      patch[2]
                    );
                  } catch {
                    console.error("applying patch failed", patch);
                    logEvent("error", {
                      type: "applying patch failed",
                      metadata: JSON.stringify({ patch }),
                    });
                    reconnectAfterSocketError();
                    return;
                  }
                }
              }
              let localActions = applicationState.localActions;
              if (applicationState.clientId === clientId) {
                localActions = applicationState.localActions.filter(
                  ([, actionMeta]) => actionMeta[0] > lastActionId
                );
              }
              if (newServerVersion !== undefined) {
                if (applicationState.clientId === clientId) {
                  localActions = applicationState.localActions
                    .filter(([, actionMeta]) => actionMeta[0] > lastActionId)
                    .map((localAction) => {
                      const [action, actionMeta] = localAction;
                      if (action[0] === ActionType.UpdatePaneCodemirror) {
                        return [
                          [action[0], { ...action[1], v: newServerVersion }],
                          actionMeta,
                        ];
                      } else {
                        return localAction;
                      }
                    });
                } else if (patches !== undefined) {
                  const codemirrorUpdatesByPaneId: {
                    [paneId: string]: {
                      [paneField: string]: {
                        changes: ChangeSet;
                        clientId: number;
                      }[];
                    };
                  } = {};
                  patches.forEach((p) => {
                    if (p[0] === PatchOp.CodemirrorUpdate) {
                      const [, [_, paneIdElement, paneField], value] = p;
                      assert(
                        Array.isArray(paneIdElement) &&
                          typeof paneField === "string"
                      );
                      const [paneId] = paneIdElement;
                      if (codemirrorUpdatesByPaneId[paneId] === undefined) {
                        codemirrorUpdatesByPaneId[paneId] = {};
                      }
                      if (
                        codemirrorUpdatesByPaneId[paneId][paneField] ===
                        undefined
                      ) {
                        codemirrorUpdatesByPaneId[paneId][paneField] = [];
                      }
                      codemirrorUpdatesByPaneId[paneId][paneField].push({
                        changes: ChangeSet.fromJSON(value.c),
                        clientId: value.cl,
                      });
                    }
                  });
                  const remoteChangesByPaneId: {
                    [paneId: string]: {
                      [paneField: string]: ChangeSet;
                    };
                  } = {};
                  // adapted from https://github.com/codemirror/collab/blob/cffa435ca5a7a0b3f46f59afdb45db4c9765b54e/src/collab.ts#L84
                  for (const paneId in codemirrorUpdatesByPaneId) {
                    for (const paneField in codemirrorUpdatesByPaneId[paneId]) {
                      const codemirrorUpdates =
                        codemirrorUpdatesByPaneId[paneId][paneField];
                      const remoteChanges: ChangeSet = codemirrorUpdates.reduce(
                        (ch, u) => ch.compose(u.changes),
                        ChangeSet.empty(codemirrorUpdates[0].changes.length)
                      );
                      if (remoteChangesByPaneId[paneId] === undefined) {
                        remoteChangesByPaneId[paneId] = {};
                      }
                      remoteChangesByPaneId[paneId][paneField] = remoteChanges;
                    }
                  }
                  let hasChange = false;
                  const updatedLocalActions = localActions.map<
                    [Action, ActionMeta]
                  >((localAction) => {
                    const [action, actionMeta] = localAction;
                    if (action[0] === ActionType.UpdatePaneCodemirror) {
                      const {
                        paneId,
                        f: paneField,
                        changes: changesJSON,
                      } = action[1];
                      const remoteChanges =
                        remoteChangesByPaneId[paneId]?.[paneField];
                      const changes = ChangeSet.fromJSON(changesJSON);
                      let updatesChanges = changes;
                      if (remoteChanges !== undefined) {
                        updatesChanges = changes.map(remoteChanges);
                        remoteChangesByPaneId[paneId][paneField] =
                          remoteChanges.map(changes, true);
                      }
                      hasChange = true;
                      return [
                        [
                          ActionType.UpdatePaneCodemirror,
                          {
                            paneId,
                            v: newServerVersion,
                            f: paneField,
                            changes: updatesChanges.toJSON(),
                          },
                        ],
                        actionMeta,
                      ];
                    } else {
                      return localAction;
                    }
                  });
                  if (hasChange) {
                    localActions = updatedLocalActions;
                  }

                  for (const paneId in remoteChangesByPaneId) {
                    for (const paneField in remoteChangesByPaneId[paneId]) {
                      const remoteChanges =
                        remoteChangesByPaneId[paneId][paneField];
                      PaneEditorRemoteUpdateEmitter.emit(paneId, {
                        paneField,
                        changes: ["changes", remoteChanges],
                      });
                    }
                  }
                }
                applicationState.server = [newServerValue, newServerVersion];
              }
              if (presenceUpdate !== undefined) {
                updateWithPresenceUpdate(clientId, presenceUpdate);
              }
              applicationState.localActions = localActions;
              break;
            }
            case ServerMessageType.Error: {
              const { c, m } = message[1];
              setErrorMessage(m ?? true);
              socket.close();
              break;
            }
            case ServerMessageType.ActionMismatchVersion: {
              const { v: serverVersion } = message[1];
              // we could have rebased while receiving this message
              if (
                applicationState.localActions.some(
                  ([action]) =>
                    action[0] === ActionType.UpdatePaneCodemirror &&
                    action[1].v < serverVersion
                )
              ) {
                // just reconnect, which will include the version we need updates from
                // can optimize this but not a priority
                socket.reconnect();
              }
              break;
            }
            case ServerMessageType.SetPresence: {
              const { cl: clientId, p: presenceUpdate, id } = message[1];
              runInAction(() => {
                if (presenceUpdate === null) {
                  delete presenceState[clientId];
                } else {
                  presenceState[clientId] = presenceUpdate;
                }
                if (id === true) {
                  delete presenceState[UNKNOWN_CLIENT_ID];
                }
              });
              break;
            }
          }
        })
      );
      const socketPingInterval = setInterval(() => {
        socket.send(SOCKET_PING_MESSAGE);
      }, 30 * 1000);

      // use a short debounce to batch push actions
      const pushLocalActionsDebounced = debounce((localActions) => {
        if (localActions.length > 0) {
          socket.send(
            JSON.stringify([
              ClientMessageType.Push,
              { a: localActions },
            ] as ClientMessage)
          );
        }
      }, 20);

      const unsubscribeFocusedPane = reaction(
        () => focusedPaneIdAtom.value,
        (focusedPaneId) => {
          canvasDispatch([ActionType.PresencePaneId, focusedPaneId ?? null]);
        }
      );

      const unsubscribe = reaction(
        () => applicationState.localActions,
        (localActions) => {
          pushLocalActionsDebounced(localActions);
        },
        {
          equals: areArraysEqual,
        }
      );

      return () => {
        clearInterval(socketPingInterval);
        unsubscribe();
        unsubscribeFocusedPane();
        socket.close();
      };
    }),
    []
  );

  useEffect(
    () =>
      reaction(
        () => applicationState.canvas,
        (canvas) => {
          canvasAtom.value = canvas;
        }
      ),
    []
  );
  const loadedCanvasId = computed(() => applicationState.server?.[0].id).get();

  return (
    <>
      {loadedCanvasId !== undefined ? (
        <PaneCanvas canvasId={loadedCanvasId} isSynced={true} />
      ) : null}
      {errorMessage !== undefined ? (
        <div className="fixed right-4 top-14 bg-red-500 text-white p-4 rounded min-w-[16rem] z-99999">
          <div className="mb-4">sorry! something went wrong...</div>
          {Array.isArray(errorMessage) ? (
            <div className="mb-4 select-text">{errorMessage[0]}</div>
          ) : typeof errorMessage === "string" ? (
            <div className="mb-4 select-text">{errorMessage}</div>
          ) : null}
          {Array.isArray(errorMessage) ? (
            <button
              onClick={() => {
                socketRef.current?.reconnect();
                setErrorMessage(undefined);
              }}
              className="bg-white hover:bg-red-50 px-4 py-2 rounded text-red-500 transition-colors"
            >
              try connecting
            </button>
          ) : (
            <button
              onClick={() => {
                ParentComlinkAPI.reload();
              }}
              className="bg-neutral-0 hover:bg-red-50 px-4 py-2 rounded text-red-500 transition-colors"
            >
              reload
            </button>
          )}
        </div>
      ) : null}
    </>
  );
}
const SocketApp = observer(SocketAppImpl);

function AppImpl() {
  const [canvasData, setCanvasData] = useState<CanvasData | undefined>(
    undefined
  );
  const isSafeMode = isSafeModeAtom.value;
  const [loadError, setLoadError] = useState<Error | undefined>(undefined);

  const rootRef = useRef<HTMLDivElement | null>(null);
  // TODO: this causes bad rendering performance
  // useEffect(() => {
  //   const rootElement = rootRef.current!;
  //   let currentBoundaryElement: HTMLElement | undefined;
  //   function onSelectStart(e: Event) {
  //     if (currentBoundaryElement === undefined && e.target !== null) {
  //       const boundaryElement = getParentByClassName(
  //         // @ts-expect-error
  //         e.target,
  //         "select-boundary"
  //       );
  //       if (boundaryElement !== undefined) {
  //         boundaryElement.classList.add("select-text");
  //         rootElement.classList.add("select-none");
  //         currentBoundaryElement = boundaryElement;
  //       }
  //     }
  //   }
  //   function onMouseUp() {
  //     if (currentBoundaryElement !== undefined) {
  //       currentBoundaryElement.classList.remove("select-text");
  //       rootElement.classList.remove("select-none");
  //       currentBoundaryElement = undefined;
  //     }
  //   }
  //   rootElement.addEventListener("selectstart", onSelectStart);
  //   rootElement.addEventListener("mouseup", onMouseUp);
  //   return () => {
  //     rootElement.removeEventListener("selectstart", onSelectStart);
  //     rootElement.removeEventListener("mouseup", onMouseUp);
  //   };
  // }, []);

  useEffect(() => {
    const uninitialize = initializeSandboxFrame(setLoadError, (canvasData) => {
      if (canvasData?.[0] === "not-sync") {
        canvasAtom.value = canvasData[1];
      }
      setCanvasData(canvasData);
    });
    return () => {
      uninitialize();
    };
  }, []);

  return (
    <TooltipProvider delayDuration={300} skipDelayDuration={300}>
      <div ref={rootRef}>
        {canvasData !== undefined ? (
          canvasData[0] === "sync" ? (
            <SocketApp key={canvasData[1] ?? canvasData[2]} />
          ) : (
            <NonSyncCanvasApp
              initialCanvas={canvasData[1]}
              key={canvasData[1].id}
            />
          )
        ) : null}
        <ClipboardPane />
        <ViewportMarksPanel />
        <TemplateBrowserPane />
        <AboutButton />
        <OpenAISetupDialogController />
        <CommandBar />
        {isSafeMode ? (
          <div className="absolute right-4 bottom-4 md:bottom-auto md:top-14 bg-neutral-800 text-neutral-0 p-4 rounded shadow-md z-1">
            <div className="mb-4">
              You are in safe mode. Panes will not autorun.
            </div>
            <div>
              <button
                onClick={action(() => {
                  isSafeModeAtom.value = false;
                })}
                className="px-4 py-2 font-medium bg-primary-500 text-neutral-0 hover:bg-primary-600 rounded transition-colors"
              >
                turn off safe mode
              </button>
            </div>
          </div>
        ) : null}
        {loadError !== undefined ? (
          <div className="absolute right-4 top-14 bg-red-500 text-neutral-0 p-4 rounded">
            error: {loadError.message}
          </div>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
export const SandboxApp = observer(AppImpl);
